import { useCallback, useEffect, useRef, useState } from "react";
import PortalLayout from "../components/PortalLayout";
import { api, formatApiErrorDetail, formatDate } from "../lib/api";
import { useI18n } from "../i18n/LanguageContext";
import { sanitizedHtml } from "../lib/sanitize";

const STATUS_STYLE = {
  pending: { color: "var(--br-text-dim)", border: "var(--br-line-bright)" },
  submitted: { color: "var(--br-bronze)", border: "var(--br-bronze)" },
  approved: { color: "var(--br-bronze-soft)", border: "var(--br-bronze-soft)" },
  rejected: { color: "var(--br-red)", border: "var(--br-red)" },
};

export default function KycPage() {
  const { t, locale } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [signing, setSigning] = useState(null); // doc object pending click-to-sign
  const [busyDocType, setBusyDocType] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kyc/me");
      setData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openTemplate = (docType) => {
    const url = `${api.defaults.baseURL}/kyc/template/${docType}`;
    window.open(url, "_blank", "noopener");
  };

  const openSignedCopy = (id) => {
    const url = `${api.defaults.baseURL}/kyc/file/${id}`;
    window.open(url, "_blank", "noopener");
  };

  const confirmSign = async (doc) => {
    setBusyDocType(doc.doc_type);
    setError("");
    try {
      await api.post("/kyc/sign", { doc_type: doc.doc_type, accept: true });
      setSigning(null);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyDocType("");
    }
  };

  const uploadFile = async (docType, file) => {
    setBusyDocType(docType);
    setError("");
    const form = new FormData();
    form.append("doc_type", docType);
    form.append("file", file);
    try {
      await api.post("/kyc/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyDocType("");
    }
  };

  const progress = data?.progress;
  const docs = data?.documents || [];

  return (
    <PortalLayout>
      <section
        className="max-w-[1080px] mx-auto px-6 sm:px-8 pt-14 pb-8"
        data-testid="kyc-page"
      >
        <div className="br-fade-up">
          <div className="br-overline" data-testid="kyc-overline">
            {t("kyc.overline")}
          </div>
          <h1
            className="font-serif-display text-[clamp(2.4rem,4.5vw,3.8rem)] mt-3 leading-[1.05]"
            data-testid="kyc-title"
          >
            {t("kyc.title")}
          </h1>
          <p className="text-[1.02rem] text-[color:var(--br-text)] mt-4 max-w-[700px] leading-relaxed">
            {t("kyc.intro")}
          </p>
        </div>

        {/* Progress */}
        {progress && (
          <div
            className="mt-10 br-card p-6 br-fade-up br-delay-1"
            data-testid="kyc-progress-card"
          >
            <div className="flex items-baseline justify-between">
              <div className="br-overline">{t("kyc.progress_label")}</div>
              <div
                className="font-mono-fin text-[0.78rem] tracking-[0.18em]"
                style={{ color: "var(--br-bronze)" }}
                data-testid="kyc-progress-value"
              >
                {t("kyc.progress_value", {
                  approved: progress.approved,
                  total: progress.total,
                })}
              </div>
            </div>
            <div
              className="relative h-px w-full mt-4"
              style={{ background: "var(--br-line-bright)" }}
            >
              <div
                className="absolute left-0 top-0 h-px transition-all duration-700"
                style={{
                  width: `${(progress.approved / progress.total) * 100}%`,
                  background: "var(--br-bronze)",
                }}
              />
            </div>
            {progress.complete && (
              <div
                className="mt-5 text-[0.92rem] font-serif-display italic"
                style={{ color: "var(--br-bronze-soft)" }}
                data-testid="kyc-complete-banner"
              >
                ✓ {t("kyc.complete_banner")}
              </div>
            )}
          </div>
        )}

        {error && (
          <div
            className="mt-6 br-card p-4 text-[color:var(--br-red)]"
            data-testid="kyc-error"
          >
            {String(error)}
          </div>
        )}

        {/* Documents list */}
        <div className="mt-8 space-y-4 br-fade-up br-delay-2" data-testid="kyc-docs-list">
          {docs.map((d) => (
            <KycDocRow
              key={d.doc_type}
              d={d}
              locale={locale}
              busy={busyDocType === d.doc_type}
              onOpenTemplate={() => openTemplate(d.doc_type)}
              onOpenSigned={() => d.id && openSignedCopy(d.id)}
              onSign={() => setSigning(d)}
              onUpload={(file) => uploadFile(d.doc_type, file)}
            />
          ))}
        </div>
      </section>

      {signing && (
        <SignModal
          doc={signing}
          busy={busyDocType === signing.doc_type}
          onCancel={() => setSigning(null)}
          onConfirm={() => confirmSign(signing)}
        />
      )}
    </PortalLayout>
  );
}

function KycDocRow({ d, locale, busy, onOpenTemplate, onOpenSigned, onSign, onUpload }) {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const statusStyle = STATUS_STYLE[d.status] || STATUS_STYLE.pending;
  const statusLabel = t(`kyc.status_${d.status}`);
  const kindLabel = t(`kyc.kind_${d.kind}`);

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    e.target.value = "";
  };

  return (
    <div
      className="br-card p-6"
      data-testid={`kyc-doc-row-${d.doc_type}`}
      style={
        d.status === "rejected"
          ? { borderLeft: "3px solid var(--br-red)", background: "rgba(179,107,107,0.04)" }
          : d.status === "approved"
          ? { borderLeft: "3px solid var(--br-bronze-soft)" }
          : undefined
      }
    >
      <div className="flex flex-wrap items-start gap-6 justify-between">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-3 flex-wrap">
            <div
              className="br-overline"
              style={{ color: "var(--br-bronze)" }}
              data-testid={`kyc-kind-${d.doc_type}`}
            >
              {kindLabel}
            </div>
            <span
              className="font-mono-fin text-[0.6rem] tracking-[0.22em] uppercase border px-2 py-0.5"
              style={{ color: statusStyle.color, borderColor: statusStyle.border }}
              data-testid={`kyc-status-${d.doc_type}`}
            >
              {statusLabel}
            </span>
          </div>
          <h3 className="font-serif-display text-[1.4rem] mt-2 text-[color:var(--br-parchment)]">
            {d.title}
          </h3>
          <p className="text-[0.9rem] text-[color:var(--br-text-dim)] mt-2 leading-relaxed">
            {d.description}
          </p>

          {d.submitted_at && (
            <div
              className="mt-3 text-[0.72rem] font-mono-fin tracking-[0.12em] text-[color:var(--br-text-dim)]"
              data-testid={`kyc-submitted-at-${d.doc_type}`}
            >
              {formatDate(d.submitted_at, locale)} · UTC
            </div>
          )}

          {d.last_comment && d.status === "rejected" && (
            <div
              className="mt-4 p-4 border-l-2"
              style={{ borderColor: "var(--br-red)", background: "rgba(179,107,107,0.04)" }}
              data-testid={`kyc-comment-${d.doc_type}`}
            >
              <div
                className="br-overline"
                style={{ color: "var(--br-red)" }}
              >
                {t("kyc.last_comment")}
              </div>
              <p className="text-[0.92rem] font-serif-display italic mt-2 text-[color:var(--br-parchment)]">
                {`\u201C${d.last_comment.text}\u201D`}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 min-w-[200px]">
          {d.kind === "sign" ? (
            <>
              <button
                onClick={onOpenTemplate}
                className="br-btn-outline"
                data-testid={`kyc-template-${d.doc_type}`}
              >
                {t("kyc.action_view_template")}
              </button>
              {d.status !== "approved" && (
                <button
                  onClick={onSign}
                  className="br-btn-primary"
                  disabled={busy}
                  data-testid={`kyc-sign-${d.doc_type}`}
                >
                  {busy
                    ? t("kyc.signing")
                    : d.has_file
                    ? t("kyc.action_resign")
                    : t("kyc.action_sign")}
                </button>
              )}
              {d.has_file && (
                <button
                  onClick={onOpenSigned}
                  className="br-btn-outline"
                  style={{ fontSize: "0.62rem" }}
                  data-testid={`kyc-download-signed-${d.doc_type}`}
                >
                  {t("kyc.action_download_signed")}
                </button>
              )}
            </>
          ) : (
            <>
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/heic,image/heif"
                className="hidden"
                onChange={onFileChange}
                data-testid={`kyc-file-input-${d.doc_type}`}
              />
              {d.status !== "approved" && (
                <button
                  onClick={() => inputRef.current?.click()}
                  className="br-btn-primary"
                  disabled={busy}
                  data-testid={`kyc-upload-${d.doc_type}`}
                >
                  {busy
                    ? t("kyc.uploading")
                    : d.has_file
                    ? t("kyc.action_replace")
                    : t("kyc.action_upload")}
                </button>
              )}
              <div className="text-[0.7rem] text-[color:var(--br-text-dim)] text-center">
                {t("kyc.upload_accept")}
              </div>
              {d.has_file && (
                <button
                  onClick={onOpenSigned}
                  className="br-btn-outline"
                  style={{ fontSize: "0.62rem" }}
                  data-testid={`kyc-download-upload-${d.doc_type}`}
                >
                  {t("kyc.action_download_uploaded")}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SignModal({ doc, busy, onCancel, onConfirm }) {
  const { t } = useI18n();
  const [accepted, setAccepted] = useState(false);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-6 py-12 overflow-y-auto"
      style={{ background: "rgba(10,14,26,0.78)", backdropFilter: "blur(8px)" }}
      onClick={onCancel}
      data-testid="kyc-sign-modal"
    >
      <div
        className="br-card br-aurora-ribbon relative max-w-[580px] w-full px-9 py-10 br-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="br-overline">{t("kyc.sign_modal_title")}</div>
        <h2 className="font-serif-display text-[1.9rem] mt-3 leading-[1.1]">
          {doc.title}
        </h2>

        <p
          className="text-[0.96rem] text-[color:var(--br-text)] mt-5 leading-relaxed"
          {...sanitizedHtml(t("kyc.sign_modal_intro", { title: doc.title }))}
        />

        <label
          className="mt-7 flex items-start gap-3 cursor-pointer p-4 border"
          style={{ borderColor: "var(--br-line-bright)" }}
          data-testid="kyc-sign-checkbox-label"
        >
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 flex-shrink-0"
            data-testid="kyc-sign-checkbox"
          />
          <span className="text-[0.92rem] text-[color:var(--br-parchment)] leading-snug">
            {t("kyc.sign_modal_checkbox")}
          </span>
        </label>

        <p className="mt-5 text-[0.75rem] text-[color:var(--br-text-dim)] leading-relaxed">
          {t("kyc.sign_modal_disclaimer")}
        </p>

        <div className="mt-7 grid grid-cols-2 gap-3">
          <button
            onClick={onCancel}
            disabled={busy}
            className="br-btn-outline"
            data-testid="kyc-sign-cancel"
          >
            {t("kyc.sign_modal_cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={!accepted || busy}
            className="br-btn-primary"
            data-testid="kyc-sign-confirm"
          >
            {busy ? t("kyc.signing") : t("kyc.sign_modal_confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
